// PR #974 (AI cockpit): explicit local action chips.
//
// These chips are the *user's* actions, not assistant suggestions. They reuse the
// same closed navigation contract the model may emit (`switch_view`, `set_filter`,
// `navigate`) and queue through the same bus, but they are produced locally with
// `local:` event ids (see `enqueueLocalChatClientAction`) and are clearly labelled
// as the person's own quick actions. Nothing here is a mutation: the contract has
// no write action at all, and every payload is re-validated by the bus allowlist
// before the executor navigates.
//
// `switch_view` and `set_filter` stay dormant on the server on purpose. They
// address page state that already lives in the URL (`DealKanbanPage` reads `view`,
// `ContactsPage` reads `has`), so a chip can drive it without any new plumbing and
// without pretending the assistant emitted anything.

import { useTranslation } from '@govard/i18n-core/react';
import { Button } from 'antd';
import { Columns3, LayoutGrid, List, ListChecks, Mail } from 'lucide-react';
import type { ReactElement } from 'react';
import type { AiChatClientAction } from '@govard/ai-chat-kit';

import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { enqueueLocalChatClientAction } from '../lib/chatClientActionBus.js';
import { chatCockpitSection, type ChatCockpitContext } from '../lib/chatCockpitContext.js';

interface CockpitActionChip {
  readonly key: string;
  readonly labelKey: string;
  readonly action: AiChatClientAction;
  readonly icon: ReactElement;
}

export interface CockpitActionChipsProps {
  context: ChatCockpitContext;
}

function buildChips(section: 'deals' | 'contacts'): CockpitActionChip[] {
  if (section === 'deals') {
    return [
      {
        key: 'view-kanban',
        labelKey: 'crm.ai_chat.cockpit.view.kanban',
        action: { action: 'switch_view', route: 'deals', view: 'kanban' },
        icon: <LayoutGrid size={13} />,
      },
      {
        key: 'view-list',
        labelKey: 'crm.ai_chat.cockpit.view.list',
        action: { action: 'switch_view', route: 'deals', view: 'list' },
        icon: <List size={13} />,
      },
      {
        key: 'view-cards',
        labelKey: 'crm.ai_chat.cockpit.view.cards',
        action: { action: 'switch_view', route: 'deals', view: 'cards' },
        icon: <Columns3 size={13} />,
      },
      {
        key: 'navigate-deals',
        labelKey: 'crm.ai_chat.cockpit.navigate.deals',
        action: { action: 'navigate', route: 'deals' },
        icon: <ListChecks size={13} />,
      },
    ];
  }

  return [
    {
      key: 'filter-has-deals',
      labelKey: 'crm.ai_chat.cockpit.filter.deals',
      action: { action: 'set_filter', route: 'contacts', filter: { key: 'has', value: 'deals' } },
      icon: <ListChecks size={13} />,
    },
    {
      key: 'filter-has-email',
      labelKey: 'crm.ai_chat.cockpit.filter.email',
      action: { action: 'set_filter', route: 'contacts', filter: { key: 'has', value: 'email' } },
      icon: <Mail size={13} />,
    },
    {
      key: 'navigate-contacts',
      labelKey: 'crm.ai_chat.cockpit.navigate.contacts',
      action: { action: 'navigate', route: 'contacts' },
      icon: <ListChecks size={13} />,
    },
  ];
}

export function CockpitActionChips({ context }: CockpitActionChipsProps): ReactElement | null {
  useI18nSubscription();
  const { t } = useTranslation();
  const section = chatCockpitSection(context.route);
  // Only sections with a real, URL-backed local action surface get chips.
  if (section !== 'deals' && section !== 'contacts') return null;

  return (
    <div
      role="group"
      aria-label={t('crm.ai_chat.cockpit.actions_group_label')}
      className="flex flex-wrap gap-1.5"
      data-testid="crm-chat-cockpit-chips"
    >
      {buildChips(section).map((chip) => (
        <Button
          key={chip.key}
          size="small"
          icon={chip.icon}
          title={t(chip.labelKey)}
          data-testid={`crm-cockpit-chip-${chip.key}`}
          onClick={() => {
            enqueueLocalChatClientAction({ action: chip.action });
          }}
        >
          {t(chip.labelKey)}
        </Button>
      ))}
    </div>
  );
}
